import { AnimatePresence, motion } from 'framer-motion';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import { ChevronDownIconVariant } from '../../../../../assets/icons/chevron-down-icon';
import { ChevronDownIcon, InfoCircle, QuestionCircle } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { Switch } from '../../../../../components/switch/index';
import { Cursor, Tooltip } from '../../../../../components/tooltip/index';
import { QueryKey } from '../../../../../constants/index';
import { ScenarioHooks, useInvalidateQueries, useLocale } from '../../../../../hooks/index';
import { useDefaultShareClass } from '../../../../../hooks/share-class/index';
import { StringKey } from '../../../../../lang/index';
import { getTranslation } from '../../../../../utils/get-translation.util';
import { useScenarioCapTableStore } from '../../../cap-table.store';
import { useScenarioStore } from '../../../scenario-modeling.store';
import { FundraisingFormSchema } from '../../fundraising-validation';
import { LiquidityFormSchema } from '../../liquidity-validation';
import { valuationFormSchema } from '../../scenario-details.validation';
import { FundraisingScenarioKey } from '../fundraising/fundraising.types';
import { LiquidityScenarioKey } from '../liquidity/liquidity.types';
import { FormShareClassCombobox } from './form-components/share-class/form/index';
import { ShareClassValue } from './form-components/share-class/share-class.types';
import { useGrantStore } from './grant.store';
import { useSafeStore } from './safe.store';

type FormSchema = FundraisingFormSchema | LiquidityFormSchema;
type ScenarioKey = FundraisingScenarioKey | LiquidityScenarioKey;

export type ScenarioDropdownProps = {
  children: ReactNode;
  className?: string;
  companyId: string;
  control: Control<FormSchema>;
  description: ReactNode | string;
  hoverText: ReactNode | string;
  isSelectChecked?: boolean;
  itemsCount: number;
  scenarioId: string;
  selectedCount: null | number;
  showButton?: boolean;
  title: ReactNode | string;
  type: ScenarioKey;
};

enum SwitchHandlerState {
  SELECT = 'select',
  UNSELECT = 'unselect',
}

type SelectSwitchHandler = (shareClass: null | ShareClassValue, mode?: 'all' | 'selected') => void;

const emptyDropdownMessageMap: Partial<Record<ScenarioKey, StringKey>> = {
  [LiquidityScenarioKey.CLASS_CONVERSION]: 'NO_AVAILABLE_CLASS_CONVERSIONS',
  [LiquidityScenarioKey.GRANTS]: 'NO_AVAILABLE_GRANTS',
  [LiquidityScenarioKey.SAFES]: 'NO_AVAILABLE_SAFES',
  [FundraisingScenarioKey.GRANTS]: 'NO_AVAILABLE_GRANTS',
  [FundraisingScenarioKey.SAFES]: 'NO_AVAILABLE_SAFES',
};

export const ScenarioDropdown: FC<ScenarioDropdownProps> = ({
  title,
  className,
  children,
  description,
  hoverText,
  showButton,
  itemsCount,
  companyId,
  scenarioId,
  control,
  isSelectChecked,
  type,
  selectedCount,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [selectChecked, setSelectChecked] = useState<Partial<Record<ScenarioKey, boolean>>>({});
  const { shareClass: defaultShareClass } = useDefaultShareClass();
  const [shareClass, setShareClass] = useState<null | ShareClassValue>(defaultShareClass || null);
  const { valuation, safes } = useWatch<FormSchema>({
    control,
  });
  const { messagesLocale } = useLocale();

  const { pointerBlockedStyles } = useScenarioStore();
  const { scenarioStates } = useGrantStore();
  const grantsStore = scenarioStates[scenarioId] ?? null;
  const { deselectAll, selectAll, scenarioStates: safeStates } = useSafeStore();
  const safesStore = safeStates[scenarioId] ?? null;

  const parsedValuation = valuationFormSchema.safeParse(valuation);
  const safeItemArray = Object.values(safes?.safeItems ?? {});
  const { scheduleUpdate } = useScenarioCapTableStore();
  const isChecked = selectChecked[type];
  const isValidValuation = Boolean(
    parsedValuation.success && parsedValuation.data.date && parsedValuation.data.preMoneyValuation,
  );
  const dropdownWrapperRef = useRef<HTMLDivElement | null>(null);
  const { selectAllSafes } = ScenarioHooks.useSelectAllScenarioSafes();
  const { selectAllClassConversions } = ScenarioHooks.useSelectAllScenarioClassConversions();
  const { deleteScenarioClassConversions } = ScenarioHooks.useDeleteAllScenarioClassConversions();
  const { deleteScenarioSafes } = ScenarioHooks.useDeleteAllScenarioSafes();
  const { invalidateQuery: invalidateClassConversions } = useInvalidateQueries(
    QueryKey.GET_SCENARIO_CLASS_CONVERSIONS,
  );

  const { invalidateQuery: invalidateSafes } = useInvalidateQueries(
    QueryKey.GET_SCENARIO_SAFES,
    QueryKey.CALC_SCENARIO_SAFES,
    QueryKey.CALC_SCENARIO_INVESTMENT,
    QueryKey.CALC_CUSTOM_SAFE,
  );

  const handleSelectSafes = (
    shareClass: null | ShareClassValue,
    mode: 'all' | 'selected' = 'all',
  ) => {
    if (mode === 'all') {
      setSelectChecked((prev) => ({ ...prev, [type]: true }));
    }
    if (!isValidValuation) return;

    setShareClass(shareClass);

    if (shareClass) {
      if (mode === 'all') {
        selectAll(scenarioId, itemsCount);
      }
      selectAllSafes(
        {
          scenarioId,
          data: { shareClassId: shareClass?.id || '' },
          onlySelected: mode === 'selected',
        },
        {
          onSuccess: () => {
            scheduleUpdate(scenarioId);
            invalidateSafes();
            invalidateClassConversions();
            setOpen(true);
          },
          // Refused as a group (e.g. owning 100 % or more): none was added, so switch it back off.
          onError: () => {
            if (mode !== 'all') return;
            deselectAll(scenarioId);
            setSelectChecked((prev) => ({ ...prev, [type]: false }));
          },
        },
      );
    }
  };

  const handleSelectClassConversions = (shareClass: null | ShareClassValue) => {
    setSelectChecked((prev) => ({ ...prev, [type]: true }));
    if (!isValidValuation) return;

    setShareClass(shareClass);
    if (shareClass) {
      scheduleUpdate(scenarioId);

      selectAllClassConversions(
        { scenarioId },
        {
          onSuccess: () => {
            invalidateClassConversions();
            setOpen(true);
          },
        },
      );
    }
  };

  const handleUnselectSafes = () => {
    deselectAll(scenarioId);
    setSelectChecked((prev) => ({ ...prev, [type]: false }));
    scheduleUpdate(scenarioId);
    deleteScenarioSafes(
      { scenarioId },
      {
        onSuccess: () => {
          invalidateSafes();
        },
      },
    );
  };

  const handleUnselectClassConversions = () => {
    setSelectChecked((prev) => ({ ...prev, [type]: false }));
    scheduleUpdate(scenarioId);
    deleteScenarioClassConversions(
      { scenarioId },
      { onSuccess: () => invalidateClassConversions() },
    );
  };

  const selectSwitchHandlerMap: Record<
    SwitchHandlerState,
    Partial<Record<ScenarioKey, SelectSwitchHandler>>
  > = {
    [SwitchHandlerState.SELECT]: {
      [LiquidityScenarioKey.CLASS_CONVERSION]: handleSelectClassConversions,
      [FundraisingScenarioKey.SAFES]: handleSelectSafes,
      [LiquidityScenarioKey.SAFES]: handleSelectSafes,
    },
    [SwitchHandlerState.UNSELECT]: {
      [LiquidityScenarioKey.CLASS_CONVERSION]: handleUnselectClassConversions,
      [FundraisingScenarioKey.SAFES]: handleUnselectSafes,
      [LiquidityScenarioKey.SAFES]: handleUnselectSafes,
    },
  };

  const selectedCountMap: Record<ScenarioKey, null | number> = {
    [LiquidityScenarioKey.CLASS_CONVERSION]: selectedCount,
    [LiquidityScenarioKey.SAFES]: safesStore?.selectedItemsCount,
    [LiquidityScenarioKey.GRANTS]: grantsStore?.selectedItemsCount,
    [FundraisingScenarioKey.CUSTOM_SAFES]: selectedCount,
    [FundraisingScenarioKey.GRANTS]: selectedCount,
    [FundraisingScenarioKey.INVESTMENT]: selectedCount,
    [FundraisingScenarioKey.POOLS]: selectedCount,
    [FundraisingScenarioKey.SAFES]: selectedCount,
  };

  const allHasSameShareClassId = safeItemArray?.every((safe, _, array) => {
    const firstShareClassId = array?.[0]?.shareClass?.id;
    const currentShareClassId = safe?.shareClass?.id;

    if (!firstShareClassId || !currentShareClassId) {
      return false;
    }

    return currentShareClassId === firstShareClassId;
  });

  useEffect(() => {
    setSelectChecked((prev) => {
      if (prev?.[type] === isSelectChecked) return prev;
      return { ...prev, [type]: isSelectChecked };
    });
    if (isSelectChecked && defaultShareClass && safes && allHasSameShareClassId) {
      const firstSafeItem = Object.values(safes?.safeItems ?? {})[0];
      const nextShareClass = {
        id: firstSafeItem?.shareClass?.id || defaultShareClass?.id || '',
        name: firstSafeItem?.shareClass?.name || defaultShareClass?.name || '',
      };
      setShareClass((prev) => {
        if (prev?.id === nextShareClass.id && prev?.name === nextShareClass.name) {
          return prev;
        }
        return nextShareClass;
      });
    }
  }, [allHasSameShareClassId, defaultShareClass, isSelectChecked, safes, type]);

  useEffect(() => {
    if (!isValidValuation) {
      setOpen(false);
    }
  }, [isValidValuation]);

  return (
    <div className={twMerge('flex w-full min-w-0 flex-col transition-all', className)}>
      <div
        className={twMerge(
          'flex w-full items-center justify-between px-4 pb-[18px] pt-3',
          isOpen ? 'rounded-t-md bg-gray-50' : 'rounded-md border-[1px] border-gray-100 bg-white',
        )}
      >
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-gray-700">{title}</span>
            <Tooltip className="w-[388px]" content={hoverText} cursor={Cursor.POINTER}>
              <InfoCircle className="size-4 cursor-pointer" iconColor="#344054" />
            </Tooltip>

            {type === LiquidityScenarioKey.CLASS_CONVERSION && (
              <Tooltip
                className="w-[388px]"
                content={messagesLocale['SCENARIO_CLASS_CONVERSION_TOOLTIP']}
                cursor={Cursor.POINTER}
              >
                <QuestionCircle />
              </Tooltip>
            )}
          </div>
          <span
            className={twJoin(
              'text-label-md font-medium text-gray-500',
              !itemsCount &&
                type !== FundraisingScenarioKey.POOLS &&
                type !== FundraisingScenarioKey.INVESTMENT &&
                type !== FundraisingScenarioKey.CUSTOM_SAFES &&
                'text-sunbeam-600',
            )}
          >
            {itemsCount ? (
              selectedCountMap[type] ? (
                `${selectedCountMap[type]} / ${itemsCount}  ${getTranslation('SELECTED')}`
              ) : (
                `${itemsCount} ${description}`
              )
            ) : emptyDropdownMessageMap?.[type] ? (
              <AppFormattedMessage id={emptyDropdownMessageMap?.[type]} />
            ) : (
              `${itemsCount} ${description}`
            )}
          </span>
        </div>

        <div className={'flex items-center gap-4'}>
          {showButton && type !== LiquidityScenarioKey.CLASS_CONVERSION && (
            <>
              <div
                className="flex items-center gap-2 rounded-lg border-[1px] border-gray-100 bg-white p-2"
                ref={dropdownWrapperRef}
              >
                <div
                  className={twMerge(
                    'rounded-[4px] bg-gray-50 px-2',
                    'py-[10px]',
                    pointerBlockedStyles,
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={isChecked}
                      disabled={!isValidValuation}
                      enabled={isChecked}
                      onChange={(state) => {
                        const handlerMapState = state
                          ? SwitchHandlerState.SELECT
                          : SwitchHandlerState.UNSELECT;

                        selectSwitchHandlerMap[handlerMapState][type]?.(
                          shareClass || defaultShareClass || null,
                          'all',
                        );
                      }}
                    />
                    <span className="text-nowrap text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id={'CONVERT_ALL'} />
                    </span>
                    <Tooltip
                      className="w-[388px]"
                      content={messagesLocale['SHARE_CLASS_POPUP']}
                      cursor={Cursor.POINTER}
                    >
                      <InfoCircle className="size-4 cursor-pointer" iconColor="#344054" />
                    </Tooltip>
                  </div>
                </div>
                {selectChecked && (
                  <FormShareClassCombobox
                    className={twMerge('max-w-[176px]', pointerBlockedStyles)}
                    companyId={companyId}
                    defaultValue={
                      shareClass || {
                        id: defaultShareClass?.id || '',
                        name: defaultShareClass?.name || '',
                      }
                    }
                    disabled={!isValidValuation}
                    setShareClass={(nextShareClass) => {
                      setShareClass(nextShareClass);
                      handleSelectSafes(nextShareClass, 'selected');
                    }}
                    wrapperClassName="border-b-gray-50"
                  />
                )}
              </div>
            </>
          )}

          {(Boolean(itemsCount) ||
            type === FundraisingScenarioKey.CUSTOM_SAFES ||
            type === FundraisingScenarioKey.INVESTMENT ||
            type === FundraisingScenarioKey.POOLS) && (
            <div
              className={twMerge(
                'h-fit w-fit p-1 transition-colors hover:bg-gray-50',
                isValidValuation ? 'cursor-pointer' : 'cursor-default',
              )}
              onClick={() => {
                isValidValuation ? setOpen((prev) => !prev) : '';
              }}
            >
              <ChevronDownIcon
                className={twMerge('size-5', isOpen ? 'rotate-180' : '', 'transition-transform')}
                iconColor={isValidValuation ? '#344054' : '#D0D5DD'}
                variant={ChevronDownIconVariant.SCENARIO}
              />
            </div>
          )}
        </div>
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            animate={{ height: 'auto', opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div className="rounded-md bg-gray-50 p-1 pt-0">
              <div className="w-full overflow-x-auto rounded-md bg-white p-3 shadow-sm">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
